import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Check, X as XIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { listReviewItems, reviewItem } from '@/services/contentLoopReview';

const CONTENT_TYPE_COLORS = {
  blog: 'bg-purple-100 text-purple-700',
  x_thread: 'bg-sky-100 text-sky-700',
  fb_post: 'bg-indigo-100 text-indigo-700',
};

const LANGUAGE_LABELS = {
  en: 'EN',
  mn: 'MN',
};

function bodyPreview(body) {
  if (!body) return '(no content)';
  const stripped = body.replace(/^#.*$/m, '').trim();
  return stripped.length > 220 ? stripped.slice(0, 220) + '…' : stripped;
}

export default function AdminContentLoop() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null); // itemId currently being approved/rejected

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listReviewItems();
      setItems(list);
    } catch (err) {
      toast.error(err.message || 'Failed to load review items.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleAction = async (itemId, action) => {
    setActingOn(itemId);
    try {
      await reviewItem(itemId, action);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      toast.success(action === 'approve' ? 'Approved — publish job created.' : 'Rejected.');
    } catch (err) {
      toast.error(err.message || `Failed to ${action} item.`);
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Content Loop Review</h1>
          <p className="text-gray-500 text-sm mt-1">
            {items.length} item{items.length === 1 ? '' : 's'} awaiting a decision
          </p>
        </div>
        <button
          onClick={fetchItems}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw size={15} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
          <p>Nothing waiting on review.</p>
          <p className="text-xs">New blog/social items from the content loop will show up here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl shadow-sm p-4 sm:p-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    CONTENT_TYPE_COLORS[item.contentType] ?? 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {item.contentType}
                </span>
                {item.ideaLanguage && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    {LANGUAGE_LABELS[item.ideaLanguage] ?? item.ideaLanguage}
                  </span>
                )}
                {item.parentItemId && (
                  <span className="text-xs text-gray-400">repurposed from {item.parentItemId}</span>
                )}
              </div>

              {item.ideaTopic && (
                <p className="text-sm font-semibold text-gray-800 mb-1">{item.ideaTopic}</p>
              )}

              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                {bodyPreview(item.body)}
              </p>

              <div className="flex items-center gap-2 pt-4 mt-3 border-t border-gray-100">
                <button
                  onClick={() => handleAction(item.id, 'approve')}
                  disabled={actingOn === item.id}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-medium bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check size={14} /> Approve
                </button>
                <button
                  onClick={() => handleAction(item.id, 'reject')}
                  disabled={actingOn === item.id}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XIcon size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
